import React from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography,
  Box, Chip, LinearProgress, Alert, Accordion, AccordionSummary, AccordionDetails,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import useDmsPalette from '../hooks/useDmsPalette';

export default function AgentReviewDialog({ open, onClose, review, onForcePublish, publishing }) {
  const MP = useDmsPalette();
  const AGENT_COLORS = {
    Sentinel: MP.yellow,
    Cortex: MP.blue,
    Babel: '#9c27b0',
    Legal: '#f44336',
  };
  if (!review) return null;

  const approved = review.approved;
  const reports = review.agentReports || {};

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {approved ? <CheckCircleIcon color="success" /> : <WarningIcon color="warning" />}
        Agent Publish Review
        <Chip
          label={`Score: ${review.overallScore}/100`}
          size="small"
          sx={{ ml: 'auto', fontWeight: 700 }}
          color={approved ? 'success' : 'warning'}
        />
      </DialogTitle>
      <DialogContent>
        {!approved && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Publishing blocked. Resolve blockers or force publish as admin.
          </Alert>
        )}

        {(review.blockers || []).map((b, i) => (
          <Alert key={i} severity="error" sx={{ mb: 1 }}>
            <strong>{b.agent}:</strong> {b.message}
          </Alert>
        ))}

        <LinearProgress
          variant="determinate"
          value={review.overallScore || 0}
          sx={{ mb: 2, height: 8, borderRadius: 1 }}
        />

        {Object.values(reports).map((report) => (
          <Accordion key={report.agent} defaultExpanded={!approved} sx={{ mb: 1 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                <Chip label={report.agent} size="small" sx={{ bgcolor: `${AGENT_COLORS[report.agent] || MP.blue}33`, color: AGENT_COLORS[report.agent] || MP.blue, fontWeight: 700 }} />
                <Typography variant="body2" color="text.secondary">{report.role}</Typography>
                <Typography variant="body2" fontWeight={700} sx={{ ml: 'auto' }}>{report.score}/100</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              {(report.issues || []).map((issue, idx) => (
                <Typography key={idx} variant="body2" sx={{ mb: 0.5 }}>
                  • [{issue.severity || 'info'}] {issue.message || issue.description}
                </Typography>
              ))}
              {(report.recommendations || []).map((rec, idx) => (
                <Typography key={`r-${idx}`} variant="caption" color="text.secondary" display="block">
                  → {rec}
                </Typography>
              ))}
            </AccordionDetails>
          </Accordion>
        ))}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        {!approved && onForcePublish && (
          <Button variant="contained" color="warning" onClick={onForcePublish} disabled={publishing}>
            {publishing ? 'Publishing…' : 'Force Publish'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}